import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Loader2, ZoomIn, Move, RotateCcw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });
}

async function getCroppedBlob(src, cropPixels, rotation = 0) {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const rot = (rotation * Math.PI) / 180;

  // draw rotated image onto a working canvas first
  const bBoxW = Math.abs(Math.cos(rot) * image.width) + Math.abs(Math.sin(rot) * image.height);
  const bBoxH = Math.abs(Math.sin(rot) * image.width) + Math.abs(Math.cos(rot) * image.height);
  const work = document.createElement("canvas");
  work.width = bBoxW;
  work.height = bBoxH;
  const wctx = work.getContext("2d");
  wctx.translate(bBoxW / 2, bBoxH / 2);
  wctx.rotate(rot);
  wctx.drawImage(image, -image.width / 2, -image.height / 2);

  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;
  ctx.drawImage(
    work,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, cropPixels.width, cropPixels.height
  );

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

export function ImageCropDialog({ open, src, aspect = 1, title = "Adjust photo", onApply, onCancel, busy }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixels, setPixels] = useState(null);

  const onComplete = useCallback((_, p) => setPixels(p), []);

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); };

  const apply = async () => {
    if (!pixels || !src) return;
    try {
      const blob = await getCroppedBlob(src, pixels, rotation);
      onApply(blob);
    } catch (e) {
      console.error("crop failed", e);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) { reset(); onCancel(); } }}
    >
      <DialogContent className="max-w-lg" data-testid="image-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{title}</DialogTitle>
        </DialogHeader>

        <div className="relative w-full h-[320px] bg-[#0b0b0a] rounded-2xl overflow-hidden">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onComplete}
            />
          )}
          <span className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1 text-[11px] text-white/90">
            <Move className="h-3 w-3" /> Drag to reposition
          </span>
        </div>

        <div className="space-y-4 mt-4">
          <div className="flex items-center gap-3">
            <ZoomIn className="h-4 w-4 text-[#9a7b38] shrink-0" />
            <Slider
              min={1} max={3} step={0.01} value={[zoom]}
              onValueChange={(v) => setZoom(v[0])}
              data-testid="crop-zoom-slider"
            />
          </div>
          <div className="flex items-center gap-3">
            <RotateCcw className="h-4 w-4 text-[#9a7b38] shrink-0" />
            <Slider
              min={0} max={360} step={1} value={[rotation]}
              onValueChange={(v) => setRotation(v[0])}
              data-testid="crop-rotate-slider"
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => { reset(); onCancel(); }} className="rounded-full" data-testid="crop-cancel">
            Cancel
          </Button>
          <Button onClick={apply} disabled={busy} className="bg-[#131311] hover:bg-[#9a7b38] rounded-full" data-testid="crop-apply">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply & Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
